import { useEffect } from 'react';
import { useParams } from 'react-router';
import {
  Button,
  Center,
  Column,
  Row,
  Screen,
  Spinner,
  StepperIndicator,
  Typography,
} from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { FormProvider } from 'react-hook-form';
import { BackButton, Divider } from '~/commonComponents';
import {
  BasicInfoForm,
  EarningBenefitForm,
  GeneralErrorMessage,
  InfoSection,
  JobDetailsFormMapper,
  useJobDetailsForm,
} from '../JobDetailsForm';
import { JobDescriptionForm } from '../JobDetailsForm/components/JobDescription';
import { ScheduleForm } from '../JobDetailsForm/components/ScheduleInfo';
import { ConfirmJobDetailsExitDialog } from './components';
import { useConfirmJobDetails, useConfirmJobDetailsExit, useDraftJobPost } from './hooks';
import { TCreatePermanentJobPostingPageRoutes } from './Types';

/** Three-step Copy & Paste flow: the office is mid-flow on "Confirm Details". */
const CONFIRM_STEPS = ['Import', 'Confirm Details', 'Payment'];
const CONFIRM_STEP_INDEX = 1;

/**
 * Confirm Job Details — single-page review screen for the Copy & Paste (import job
 * description) flow.
 *
 * Composes the existing JobDetailsForm section components onto one editable page:
 * Job Information, Schedule & Pay (incl. Stated Pay and optional Benefits), and Job
 * Description. The page hydrates from the draft posting created (and pre-filled with the
 * parsed fields) when the office clicked "Add" — fields that could not be extracted simply
 * render blank, with no detected/missing indicators, no field-count summary, and no green
 * banner.
 *
 * The stepper sits on the top row beside "Back"; sections are borderless. "Back" returns
 * to the previous screen; "Next" validates the whole form, saves the reviewed values onto
 * the draft, and continues into the existing Review & Payment step.
 */
export const ConfirmJobDetailsPage = () => {
  const { id } = useParams<TCreatePermanentJobPostingPageRoutes>();
  const { draft, isLoading } = useDraftJobPost(id);

  const controller = useJobDetailsForm({ initialValues: draft });

  // RHF defaultValues only apply at mount, so re-seed the form once the draft arrives.
  useEffect(() => {
    if (draft) {
      controller.reset(JobDetailsFormMapper.fromEntity(draft));
    }
  }, [draft, controller]);

  const { onNext, isPending, hasFormErrors } = useConfirmJobDetails(controller, id);
  const { isExitConfirmOpen, requestExit, cancelExit, confirmExit } = useConfirmJobDetailsExit(
    controller.formState.isDirty,
  );

  if (isLoading) {
    return (
      <Center style={{ marginBlock: 48 }}>
        <Spinner centered variant="default" size="md" />
      </Center>
    );
  }

  return (
    <>
      <Screen variant="standard" data-testid="confirm-job-details-page">
        <Screen.Header
          start={<BackButton onClick={requestExit} />}
          title={<StepperIndicator steps={CONFIRM_STEPS} currentStep={CONFIRM_STEP_INDEX} />}
        >
          <Center>
            <Column align="center" gap={2} style={{ maxWidth: 560 }}>
              <Typography as="span" bold size="heading_2" color={colors.neutral[600]}>
                Confirm Job Details
              </Typography>
              <Typography
                as="span"
                size="body_large"
                color={colors.neutral[500]}
                textAlign="center"
              >
                We imported as much as we could. Add any more details to your posting.
              </Typography>
            </Column>
          </Center>
        </Screen.Header>
        <Screen.Body>
          <Column align="center" gap={4} style={{ paddingBottom: 28 }}>
            {hasFormErrors && <GeneralErrorMessage />}
            <FormProvider {...controller}>
              <Column width="100%" className="self-center" gap={4} style={{ maxWidth: 800 }}>
                <InfoSection
                  borderless
                  title="Job Information"
                  titleFontSize={24}
                  titleColor="neutral_600"
                >
                  <BasicInfoForm />
                </InfoSection>
                <InfoSection
                  borderless
                  title="Schedule & Pay"
                  titleFontSize={24}
                  titleColor="neutral_600"
                >
                  <Column gap={6}>
                    <ScheduleForm
                      control={controller.control}
                      errors={controller.formState.errors}
                    />
                    <Divider height={1} marginVertical={0} />
                    <EarningBenefitForm />
                  </Column>
                </InfoSection>
                <InfoSection
                  borderless
                  title="Job Description"
                  titleFontSize={24}
                  titleColor="neutral_600"
                >
                  <JobDescriptionForm
                    control={controller.control}
                    errors={controller.formState.errors}
                  />
                </InfoSection>
              </Column>
            </FormProvider>
            <Row justify="center" className="mt-6">
              <Button
                data-cy="confirm-job-details-next-button"
                onClick={onNext}
                isLoading={isPending}
                size="lg"
              >
                Next
              </Button>
            </Row>
          </Column>
        </Screen.Body>
      </Screen>
      {isExitConfirmOpen && (
        <ConfirmJobDetailsExitDialog onCancel={cancelExit} onConfirm={confirmExit} />
      )}
    </>
  );
};
